import {
  Box,
  Button,
  Heading,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Select,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react"
import axios from "axios"
import React, { useEffect, useState } from "react"
import FileUpload from "../Misc/FormComponents/FileUpload"
import { apiLink } from "config"


const API = `${apiLink}/questions`
const TOPIC_API = `${apiLink}/topics`
const SUBTOPIC_API = `${apiLink}/sub-topics`

interface Topic {
  id: number
  Name: string
  Code: string
}

interface SubTopic {
  id: number
  Name: string
  Code: string
  topic?: Topic
}

interface Question {
  id: number
  Code: string
  ItalianText: string
  SinhalaText: string
  Answer: boolean
  ItalianVoiceRecord: string
  SinhalaVoiceRecord: string
  Image: string
  sub_topic?: SubTopic
}

const Questions: React.FC = () => {
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [questions, setQuestions] = useState<Question[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [subTopics, setSubTopics] = useState<SubTopic[]>([])

  const [editId, setEditId] = useState<number | null>(null)

  const [code, setCode] = useState("")
  const [italianText, setItalianText] = useState("")
  const [sinhalaText, setSinhalaText] = useState("")
  const [answer, setAnswer] = useState(false)
  const [italianVoice, setItalianVoice] = useState("")
  const [sinhalaVoice, setSinhalaVoice] = useState("")
  const [image, setImage] = useState("")

  const [selectedTopic, setSelectedTopic] = useState<string>("")
  const [selectedSubTopic, setSelectedSubTopic] = useState<string>("")

  const [searchCode, setSearchCode] = useState("")
  const [searchItalian, setSearchItalian] = useState("")
  const [filterTopic, setFilterTopic] = useState("")
  const [filterSubTopic, setFilterSubTopic] = useState("")
  const [sortAsc, setSortAsc] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [total, setTotal] = useState(0)

  const loadAll = async () => {
    try {
      const [qRes, tRes] = await Promise.all([
        axios.get(API, {
          params: {
            _page: page,
            _limit: pageSize,
            code: searchCode,
            italian: searchItalian,
            // remove topic if filtering by subtopic only
            subTopic: filterSubTopic,
            sort: sortAsc ? "Code:asc" : "Code:desc",
          },
          withCredentials: true,
        }),

        axios.get(TOPIC_API, {
          withCredentials: true,
        }),
      ])

      setQuestions(qRes.data.data)
      setTotal(qRes.data.total)
      setTopics(tRes.data)

      // DO NOT load all subtopics here
    } catch (err) {
      console.error(err)
      toast({
        title: "Error loading data",
        status: "error",
      })
    }
  }
  const fetchFilterSubTopics = async (topicId: string) => {
    console.log("TOPIC ID:", topicId)

    if (!topicId) {
      setSubTopics([])
      return
    }

    try {
      const res = await axios.get(SUBTOPIC_API, {
        params: { topic: topicId },
        withCredentials: true,
      })

      console.log("SUBTOPIC RESPONSE:", res.data)

      setSubTopics(res.data)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    loadAll()
  }, [page, searchCode, searchItalian, filterTopic, filterSubTopic, sortAsc])

  const openAddModal = () => {
    setEditId(null)
    setCode("")
    setItalianText("")
    setSinhalaText("")
    setAnswer(false)
    setItalianVoice("")
    setSinhalaVoice("")
    setImage("")
    setSelectedTopic("")
    setSelectedSubTopic("")
    onOpen()
  }

  const openEditModal = (q: Question) => {
    setEditId(q.id)
    setCode(q.Code)
    setItalianText(q.ItalianText)
    setSinhalaText(q.SinhalaText)
    setAnswer(q.Answer)
    setItalianVoice(q.ItalianVoiceRecord)
    setSinhalaVoice(q.SinhalaVoiceRecord)
    setImage(q.Image)
    setSelectedTopic(q.sub_topic?.topic?.id?.toString() || "")
    setSelectedSubTopic(q.sub_topic?.id?.toString() || "")
    onOpen()
  }

  const handleSubmit = async () => {
    if (!selectedSubTopic) {
      toast({ title: "Please select a sub topic", status: "warning" })
      return
    }

    const data = {
      Code: code,
      ItalianText: italianText,
      SinhalaText: sinhalaText,
      Answer: answer,
      ItalianVoiceRecord: italianVoice,
      SinhalaVoiceRecord: sinhalaVoice,
      Image: image,
      sub_topic: selectedSubTopic,
    }

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, data, {
          withCredentials: true,
        })
        toast({ title: "Updated", status: "success" })
      } else {
        await axios.post(API, data, {
          withCredentials: true,
        })
        toast({ title: "Added", status: "success" })
      }
      loadAll()
      onClose()
    } catch (err) {
      console.error(err)
      toast({ title: "Error saving", status: "error" })
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API}/${id}`, {
        withCredentials: true,
      })
      toast({ title: "Deleted", status: "info" })
      loadAll()
    } catch (err) {
      console.error(err)
      toast({ title: "Delete failed", status: "error" })
    }
  }

  return (
    <Box p={5} boxShadow="md" bg="white" borderRadius="md">
      <Heading size="md" mb={4}>
        Questions
      </Heading>

      <Button colorScheme="blue" onClick={openAddModal} mb={5}>
        Add Question
      </Button>

      <Box mb={4}>
        {/* Row 1 */}
        <HStack spacing={3} mb={3}>
          <Input
            placeholder="Search Code"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
          />

          <Select
            placeholder="Filter by Topic"
            value={filterTopic}
            onChange={(e) => {
              const topicId = e.target.value

              setFilterTopic(topicId)
              setFilterSubTopic("")

              fetchFilterSubTopics(topicId)
            }}
          >
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.Name}
              </option>
            ))}
          </Select>

          <Select
            placeholder="Filter by Sub Topic"
            value={filterSubTopic}
            onChange={(e) => setFilterSubTopic(e.target.value)}
          >
            {subTopics.map((st) => (
              <option key={st.id} value={st.id}>
                {st.Name}
              </option>
            ))}
          </Select>
        </HStack>

        {/* Row 2 */}
        <Input
          placeholder="Search Italian Text"
          value={searchItalian}
          onChange={(e) => setSearchItalian(e.target.value)}
        />
      </Box>

      <Table variant="simple">
        <Thead bg="gray.100">
          <Tr>
            <Th cursor="pointer" onClick={() => setSortAsc(!sortAsc)}>
              Code {sortAsc ? "▲" : "▼"}
            </Th>
            <Th>Topic</Th>
            <Th>Sub Topic</Th>
            <Th>Italian</Th>
            <Th>Sinhala</Th>
            <Th>Answer</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {questions.length ? (
            questions.map((q) => (
              <Tr key={q.id}>
                <Td>{q.Code}</Td>
                <Td>{q.sub_topic?.topic?.Name || "—"}</Td>
                <Td>{q.sub_topic?.Name || "—"}</Td>
                <Td>{q.ItalianText}</Td>
                <Td>{q.SinhalaText}</Td>
                <Td>{q.Answer ? "True" : "False"}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="sm"
                      colorScheme="yellow"
                      onClick={() => openEditModal(q)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(q.id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={7} textAlign="center" py={10} color="gray.500">
                No Questions Yet
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <HStack mt={4}>
        <Button size="sm" onClick={() => setPage((p) => Math.max(p - 1, 1))}>
          Prev
        </Button>

        <Text>
          Page {page} / {Math.ceil(total / pageSize)}
        </Text>

        <Button
          size="sm"
          onClick={() => setPage((p) => p + 1)}
          isDisabled={page * pageSize >= total}
        >
          Next
        </Button>
      </HStack>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editId ? "Edit Question" : "Add Question"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={1}>
              Topic
            </Text>
            <Select
              placeholder="Select Topic"
              mb={3}
              value={selectedTopic}
              onChange={(e) => {
                const topicId = e.target.value

                setSelectedTopic(topicId)
                setSelectedSubTopic("")
                setCode("")

                fetchFilterSubTopics(topicId)
              }}
            >
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.Name}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" mb={1}>
              Subtopic
            </Text>
            <Select
              placeholder="Select Sub Topic"
              mb={3}
              value={selectedSubTopic}
              onChange={(e) => {
                const subTopicId = e.target.value
                setSelectedSubTopic(subTopicId)
              }}
            >
              {subTopics.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.Code} - {st.Name}
                </option>
              ))}
            </Select>

            <Text fontSize="sm" mb={1}>
              Question Code
            </Text>
            <Input
              placeholder="Enter Question Code"
              mb={3}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Text fontSize="sm" mb={1}>
              Italian Text
            </Text>
            <Textarea
              placeholder="Italian Text"
              mb={3}
              value={italianText}
              onChange={(e) => setItalianText(e.target.value)}
            />

            <Text fontSize="sm" mb={1}>
              Sinhala Text
            </Text>
            <Textarea
              placeholder="Sinhala Text"
              mb={3}
              value={sinhalaText}
              onChange={(e) => setSinhalaText(e.target.value)}
            />
            <Text fontSize="sm" mb={1}>
              Answer
            </Text>
            <Select
              mb={3}
              value={answer ? "true" : "false"}
              onChange={(e) => setAnswer(e.target.value === "true")}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>

            <Flex direction="column" mb={4}>
              {image && (
                <Image
                  src={`${apiLink}${image}`}
                  height="80px"
                  objectFit="cover"
                  mb={2}
                />
              )}
              <FileUpload
                text="Upload Image"
                handleSetFieldValue={(_, url) => setImage(url)}
                handleFieldName="Image"
              />
            </Flex>

            <Flex direction="column" mb={4}>
              {italianVoice && (
                <audio controls style={{ marginBottom: 8 }}>
                  <source src={`${apiLink}${italianVoice}`} />
                </audio>
              )}
              <FileUpload
                text="Upload Italian Voice"
                acceptFileTypes="audio/mpeg,audio/x-m4a"
                handleSetFieldValue={(_, url) => setItalianVoice(url)}
                handleFieldName="ItalianVoiceRecord"
              />
            </Flex>

            <Flex direction="column" mb={4}>
              {sinhalaVoice && (
                <audio controls style={{ marginBottom: 8 }}>
                  <source src={`${apiLink}${sinhalaVoice}`} />
                </audio>
              )}
              <FileUpload
                text="Upload Sinhala Voice"
                acceptFileTypes="audio/mpeg,audio/x-m4a"
                handleSetFieldValue={(_, url) => setSinhalaVoice(url)}
                handleFieldName="SinhalaVoiceRecord"
              />
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editId ? "Update" : "Submit"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default Questions;
